/**
 * Accordion Component - Business Value Calculator
 * Sprint 7: Day 1
 *
 * Reusable accordion component with:
 * - One-section-open-at-a-time behavior
 * - Smooth animations
 * - Keyboard navigation
 * - ARIA accessibility
 * - CSP-compliant event handlers
 */

import React, { useState, useRef } from 'react';

export interface AccordionSection {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  value?: string | number;
  children: React.ReactNode;
}

interface AccordionProps {
  sections: AccordionSection[];
  defaultOpenId?: string;
  onSectionChange?: (sectionId: string | null) => void;
}

export const Accordion: React.FC<AccordionProps> = ({
  sections,
  defaultOpenId,
  onSectionChange
}) => {
  const [openSectionId, setOpenSectionId] = useState<string | null>(defaultOpenId || null);
  const headerRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Toggle section open/close
  const toggleSection = (sectionId: string) => {
    const newOpenId = openSectionId === sectionId ? null : sectionId;
    setOpenSectionId(newOpenId);

    if (onSectionChange) {
      onSectionChange(newOpenId);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, sectionId: string) => {
    const sectionIds = sections.map(s => s.id);
    const currentIndex = sectionIds.indexOf(sectionId);

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleSection(sectionId);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % sectionIds.length;
      const nextId = sectionIds[nextIndex];
      headerRefs.current.get(nextId)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + sectionIds.length) % sectionIds.length;
      const prevId = sectionIds[prevIndex];
      headerRefs.current.get(prevId)?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      const firstId = sectionIds[0];
      headerRefs.current.get(firstId)?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      const lastId = sectionIds[sectionIds.length - 1];
      headerRefs.current.get(lastId)?.focus();
    }
  };

  // Format value display
  const formatValue = (value: string | number | undefined): string => {
    if (value === undefined) return '';

    if (typeof value === 'number') {
      return `$${value.toLocaleString()}`;
    }

    return value;
  };

  return (
    <div className="accordion-group" role="region" aria-label="Cost sections">
      {sections.map((section) => {
        const isOpen = openSectionId === section.id;

        return (
          <div
            key={section.id}
            className={`accordion-item ${isOpen ? 'active' : ''}`}
          >
            {/* Header */}
            <div
              ref={(el) => {
                if (el) {
                  headerRefs.current.set(section.id, el);
                }
              }}
              className="accordion-header"
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              aria-controls={`accordion-body-${section.id}`}
              onClick={() => toggleSection(section.id)}
              onKeyDown={(e) => handleKeyDown(e, section.id)}
            >
              {/* Icon */}
              <div className="accordion-icon" aria-hidden="true">
                {section.icon}
              </div>

              {/* Title Group */}
              <div className="accordion-title-group">
                <h3 className="accordion-title">{section.title}</h3>
                {section.subtitle && (
                  <p className="accordion-subtitle">{section.subtitle}</p>
                )}
              </div>

              {/* Value Display */}
              {section.value !== undefined && (
                <div className="accordion-value" aria-label={`Total: ${formatValue(section.value)}`}>
                  {formatValue(section.value)}
                </div>
              )}

              {/* Chevron */}
              <svg
                className="accordion-chevron"
                aria-hidden="true"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {/* Body */}
            <div
              id={`accordion-body-${section.id}`}
              className="accordion-body"
              role="region"
              aria-labelledby={`accordion-header-${section.id}`}
              hidden={!isOpen}
            >
              {section.children}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
